import React, { useState } from 'react';

const inputStyle = {
  width: '100%',
  background: 'transparent',
  border: 'none',
  borderBottom: '2px solid rgba(156, 163, 175, 0.3)',
  borderRadius: 0,
  padding: '10px 0',
  fontFamily: 'var(--font-body)',
  fontSize: '14px',
  fontWeight: 400,
  color: 'var(--color-foreground)',
  outline: 'none',
  transition: 'border-color 250ms ease-out',
  letterSpacing: '0.01em',
};

const labelStyle = {
  fontFamily: 'var(--font-caption)',
  fontSize: '11px',
  fontWeight: 500,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--color-muted-foreground)',
  display: 'block',
  marginBottom: '8px',
};

const getStrength = (password) => {
  if (!password) return { score: 0, label: '', color: 'transparent' };
  let score = 0;
  if (password?.length >= 8) score++;
  if (password?.length >= 12) score++;
  if (/[A-Z]/?.test(password)) score++;
  if (/[0-9]/?.test(password)) score++;
  if (/[^A-Za-z0-9]/?.test(password)) score++;
  if (score <= 1) return { score, label: 'Svak', color: '#EF4444' };
  if (score <= 2) return { score, label: 'Middels', color: '#F59E0B' };
  if (score <= 3) return { score, label: 'God', color: '#10B981' };
  return { score, label: 'Sterk', color: '#10B981' };
};

const ResetPasswordForm = ({ userEmail, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const strength = getStrength(newPassword);

  const validate = () => {
    const errs = {};
    if (!newPassword) errs.newPassword = 'Nytt passord er påkrevd';
    else if (newPassword?.length < 8) errs.newPassword = 'Passord må være minst 8 tegn';
    if (!confirmPassword) errs.confirmPassword = 'Bekreft passord er påkrevd';
    else if (newPassword !== confirmPassword) errs.confirmPassword = 'Passordene stemmer ikke overens';
    return errs;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 900));
    // Update password in localStorage
    try {
      const users = JSON.parse(localStorage.getItem('veiassistent_users') || '[]');
      const updatedUsers = users?.map(u =>
        u?.email?.toLowerCase() === userEmail?.toLowerCase() ? { ...u, password: newPassword } : u
      );
      localStorage.setItem('veiassistent_users', JSON.stringify(updatedUsers));
    } catch {}
    setIsLoading(false);
    onSuccess();
  };

  const EyeIcon = ({ show }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {show ? (
        <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
      ) : (
        <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
      )}
    </svg>
  );

  return (
    <div>
      <div style={{ width: '48px', height: '3px', background: 'var(--color-accent)', marginBottom: '24px' }} />
      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '8px', lineHeight: 1.2 }}>
        Nytt passord
      </h2>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 400, color: 'var(--color-muted-foreground)', marginBottom: '8px', lineHeight: 1.6 }}>
        Opprett et nytt passord for kontoen din.
      </p>
      {userEmail && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '28px', padding: '8px 12px', background: 'rgba(0, 79, 152, 0.08)', border: '1px solid rgba(0, 79, 152, 0.2)', borderRadius: 'var(--radius-sm)' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
          <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-primary)', fontWeight: 500 }}>{userEmail}</span>
        </div>
      )}
      <form onSubmit={handleSubmit} noValidate>
        {/* New Password */}
        <div className="mb-6">
          <label htmlFor="new-password" style={labelStyle}>Nytt passord *</label>
          <div style={{ position: 'relative' }}>
            <input
              id="new-password"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={e => { setNewPassword(e?.target?.value); setErrors(prev => ({ ...prev, newPassword: '' })); }}
              placeholder="Minst 8 tegn"
              style={{ ...inputStyle, paddingRight: '36px', borderBottomColor: errors?.newPassword ? '#FF5722' : 'rgba(156, 163, 175, 0.3)' }}
              onFocus={e => { if (!errors?.newPassword) e.target.style.borderBottomColor = 'var(--color-primary)'; }}
              onBlur={e => { if (!errors?.newPassword) e.target.style.borderBottomColor = 'rgba(156, 163, 175, 0.3)'; }}
            />
            <button type="button" onClick={() => setShowNew(!showNew)} style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-muted-foreground)', padding: '4px' }}>
              <EyeIcon show={showNew} />
            </button>
          </div>
          {/* Strength bar */}
          {newPassword && (
            <div style={{ marginTop: '8px' }}>
              <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                {[1,2,3,4]?.map(i => (
                  <div key={i} style={{ flex: 1, height: '3px', borderRadius: '2px', background: i <= strength?.score ? strength?.color : 'rgba(156,163,175,0.2)', transition: 'background 200ms ease-out' }} />
                ))}
              </div>
              <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', fontWeight: 600, color: strength?.color }}>{strength?.label}</p>
            </div>
          )}
          {errors?.newPassword && (
            <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: '#FF5722', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}><span>⚠</span>{errors?.newPassword}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="mb-8">
          <label htmlFor="confirm-password" style={labelStyle}>Bekreft passord *</label>
          <div style={{ position: 'relative' }}>
            <input
              id="confirm-password"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e?.target?.value); setErrors(prev => ({ ...prev, confirmPassword: '' })); }}
              placeholder="Gjenta passord"
              style={{ ...inputStyle, paddingRight: '36px', borderBottomColor: errors?.confirmPassword ? '#FF5722' : 'rgba(156, 163, 175, 0.3)' }}
              onFocus={e => { if (!errors?.confirmPassword) e.target.style.borderBottomColor = 'var(--color-primary)'; }}
              onBlur={e => { if (!errors?.confirmPassword) e.target.style.borderBottomColor = 'rgba(156, 163, 175, 0.3)'; }}
            />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)} style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-muted-foreground)', padding: '4px' }}>
              <EyeIcon show={showConfirm} />
            </button>
          </div>
          {errors?.confirmPassword && (
            <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: '#FF5722', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}><span>⚠</span>{errors?.confirmPassword}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            width: '100%',
            background: isLoading ? 'rgba(255, 87, 34, 0.6)' : 'var(--color-accent)',
            border: '2px solid var(--color-accent)',
            borderRadius: 'var(--radius-sm)',
            padding: '14px 24px',
            fontFamily: 'var(--font-heading)',
            fontSize: '16px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#FFFFFF',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'all 250ms ease-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
          onMouseEnter={e => { if (!isLoading) { e.currentTarget.style.background = '#E64A19'; e.currentTarget.style.borderColor = '#E64A19'; } }}
          onMouseLeave={e => { if (!isLoading) { e.currentTarget.style.background = 'var(--color-accent)'; e.currentTarget.style.borderColor = 'var(--color-accent)'; } }}
        >
          {isLoading ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 11-6.219-8.56" />
              </svg>
              Oppdaterer...
            </>
          ) : 'Oppdater passord'}
        </button>
      </form>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } input::placeholder { color: rgba(156, 163, 175, 0.5); }`}</style>
    </div>
  );
};

export default ResetPasswordForm;
